import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { TrophyIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useStudentFocusSession } from '../../contexts/StudentFocusSessionContext';
import FullscreenNumberDisplay from '../../components/student/FullscreenNumberDisplay';

const CompetitionRound = () => {
  const { t } = useTranslation();
  const { competitionId, roundNumber } = useParams();
  const navigate = useNavigate();

  const [gameState, setGameState] = useState('loading'); // 'loading', 'ready', 'playing', 'answer', 'result', 'completed'
  const [sessionData, setSessionData] = useState(null);
  const [roundData, setRoundData] = useState(null);
  const [competitionSessionId, setCompetitionSessionId] = useState(null);
  const [currentNumberIndex, setCurrentNumberIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [totalRounds, setTotalRounds] = useState(null);

  const hasStartedRef = useRef(false);
  const audioContextRef = useRef(null);

  const { setFocusSession } = useStudentFocusSession();
  useEffect(() => {
    setFocusSession(true);
    return () => setFocusSession(false);
  }, [setFocusSession]);

  // صوت التصفيق للإجابة الصحيحة
  const playSuccessSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const clapCount = 6;
      const clapInterval = 0.15;
      
      for (let i = 0; i < clapCount; i++) {
        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
        
        oscillator.frequency.setValueAtTime(2000 + Math.random() * 1000, audioContextRef.current.currentTime + i * clapInterval);
        oscillator.type = 'square';
        
        gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime + i * clapInterval);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContextRef.current.currentTime + i * clapInterval + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + i * clapInterval + 0.1);
        
        oscillator.start(audioContextRef.current.currentTime + i * clapInterval);
        oscillator.stop(audioContextRef.current.currentTime + i * clapInterval + 0.1);
      }
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت "Ohhhhh" للإجابة الخاطئة
  const playEncouragementSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.setValueAtTime(150, audioContextRef.current.currentTime);
      oscillator.frequency.setValueAtTime(120, audioContextRef.current.currentTime + 0.3);
      oscillator.frequency.setValueAtTime(100, audioContextRef.current.currentTime + 0.6);
      oscillator.type = 'sawtooth';
      
      gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, audioContextRef.current.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContextRef.current.currentTime + 0.4);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 1.2);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 1.2);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت المنبه عند تغيير الأرقام
  const playBeepSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContextRef.current.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.1);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.1);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // جلب تفاصيل المسابقة للحصول على عدد الجولات
  const fetchCompetitionInfo = async () => {
    try {
      const response = await api.get(`/competitions/${competitionId}`);
      if (response.data.success) {
        setTotalRounds(response.data.data.totalRounds);
      }
    } catch (error) {
      console.error('Error fetching competition info:', error);
    }
  };

  // بدء أو استئناف الجولة
  const startRound = async () => {
    console.log('=== بدء الجولة ===');
    console.log('Competition ID:', competitionId);
    console.log('Round Number:', roundNumber);
    
    setIsLoading(true);
    try {
      const url = `/competitions/${competitionId}/rounds/${roundNumber}/start`;
      console.log('API URL:', url);
      
      const response = await api.post(url);
      console.log('✅ Round started successfully:', response.data);
      
      if (response.data.success) {
        const data = response.data.data;
        console.log('Competition Session ID:', data.sessionId);
        console.log('Flash Anzan Session:', data.flashAnzanSession);
        console.log('Round Data:', data.round);
        
        setCompetitionSessionId(data.sessionId);
        setSessionData(data.flashAnzanSession);
        setRoundData(data.round);
        
        // التحقق من حالة الجلسة
        if (data.flashAnzanSession.status === 'active') {
          setGameState('answer');
          toast.success(t('competitions.resumingSession'));
        } else {
          // جلسة جديدة - الانتقال لشاشة الاستعداد
          setGameState('ready');
          toast.success(response.data.message);
        }
        
        setIsLoading(false);
      }
    } catch (error) {
      console.error('❌ Error starting round:', error);
      console.error('Error response:', error.response?.data);
      
      // استئناف جلسة موجودة (نشطة أو created)
      const errorMessage = error.response?.data?.message || '';
      if (
        error.response?.status === 400 &&
        (errorMessage.includes('جلسة نشطة') || errorMessage.includes('جلسة لهذه الجولة'))
      ) {
        console.log('⚠️ Active session exists - attempting to resume...');
        // محاولة جلب بيانات الجلسة النشطة
        try {
          const resumeResponse = await api.get(`/competitions/${competitionId}/rounds/${roundNumber}/active-session`);
          console.log('✅ Active session data:', resumeResponse.data);
          
          if (resumeResponse.data.success) {
            const activeSession = resumeResponse.data.data;
            
            // تحويل البيانات إلى الشكل المتوقع
            const flashAnzanSession = {
              sessionId: activeSession.sessionId,
              status: 'created', // نبدأ من جديد
              operation: activeSession.operationType,
              displayTime: activeSession.displayTimeMs,
              totalNumbers: activeSession.totalNumbers,
              numbers: activeSession.numbers,
              result: String(activeSession.correctAnswer),
              isCorrect: false,
              score: "0.00"
            };
            
            const roundData = {
              operationType: activeSession.operationType,
              totalNumbers: activeSession.totalNumbers,
              digitsPerNumber: activeSession.digitsPerNumber,
              displayTimeMs: activeSession.displayTimeMs,
              roundNumber: activeSession.roundNumber
            };
            
            console.log('Transformed Flash Anzan Session:', flashAnzanSession);
            console.log('Transformed Round Data:', roundData);
            
            setCompetitionSessionId(activeSession.sessionId);
            setSessionData(flashAnzanSession);
            setRoundData(roundData);
            
            // بدء جلسة فلاش أنزان عادية
            setGameState('ready');
            toast.success(t('competitions.resumingSession'));
            setIsLoading(false);
            return; // الخروج من الدالة بنجاح - لا نعرض رسالة خطأ
          }
        } catch (resumeError) {
          console.error('❌ Failed to resume session:', resumeError);
          // في حالة فشل الاستئناف، نعرض رسالة خطأ ونخرج
          toast.error(t('competitions.fetchError'));
          setIsLoading(false);
          setTimeout(() => {
            navigate(-1);
          }, 2000);
          return;
        }
      }
      
      // عرض رسالة الخطأ فقط إذا لم يكن خطأ جلسة نشطة أو فشل الاستئناف
      toast.error(errorMessage || t('competitions.fetchError'));
      setIsLoading(false);
      
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    }
  };

  // بدء عرض الأرقام
  const startDisplaying = () => {
    setGameState('playing');
    setCurrentNumberIndex(0);
    setSessionStartTime(Date.now());
  };

  // عرض الأرقام
  useEffect(() => {
    if (gameState === 'playing' && sessionData?.numbers) {
      if (currentNumberIndex < sessionData.numbers.length) {
        playBeepSound();

        const timer = setTimeout(() => {
          setCurrentNumberIndex((prev) => prev + 1);
        }, roundData?.displayTimeMs || 1000);

        return () => clearTimeout(timer);
      }

      setGameState('answer');
    }
  }, [gameState, currentNumberIndex, sessionData, roundData]);

  const handleNumpadInput = (key) => {
    if (key === 'backspace') {
      setAnswer((prev) => prev.slice(0, -1));
    } else if (key === 'clear') {
      setAnswer('');
    } else if (key === '-') {
      setAnswer((prev) => {
        if (prev === '') return '-';
        if (prev === '-') return '';
        if (!prev.startsWith('-')) return `-${prev}`;
        return prev;
      });
    } else {
      setAnswer((prev) => {
        const newValue = prev + key;
        return /^-?\d*$/.test(newValue) ? newValue : prev;
      });
    }
  };

  // إرسال الإجابة
  const submitAnswer = useCallback(async () => {
    if (!answer.trim()) {
      toast.error(t('flashAnzan.pleaseEnterAnswer'));
      return;
    }

    setIsLoading(true);
    try {
      const totalTimeMs = Date.now() - sessionStartTime;

      const response = await api.post(`/competitions/sessions/${competitionSessionId}/submit`, {
        studentAnswer: parseInt(answer, 10),
        totalTimeMs,
      });

      if (response.data.success) {
        setResult(response.data.data);
        setGameState('result');

        if (response.data.data.isCorrect) {
          playSuccessSound();
        } else {
          playEncouragementSound();
        }

        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('❌ Error submitting answer:', error);
      const errorMessage = error.response?.data?.message || t('flashAnzan.submitError');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [answer, competitionSessionId, sessionStartTime, t]);

  // لوحة المفاتيح المباشرة في شاشة الإجابة
  useEffect(() => {
    if (gameState !== 'answer') return;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && answer) {
        submitAnswer();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setAnswer((prev) => prev.slice(0, -1));
      } else if (/^\d$/.test(e.key)) {
        setAnswer((prev) => {
          const newValue = prev + e.key;
          return /^-?\d*$/.test(newValue) ? newValue : prev;
        });
      } else if (e.key === '-' && !answer.startsWith('-')) {
        setAnswer((prev) => (prev === '' ? '-' : `-${prev}`));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, answer, submitAnswer]);

  // الانتقال للجولة التالية أو عرض شاشة الإنهاء
  const goToNextRound = () => {
    const currentRound = parseInt(roundNumber);
    
    console.log('=== التحقق من الجولة التالية ===');
    console.log('Current Round:', currentRound);
    console.log('Total Rounds:', totalRounds);
    
    // التحقق إذا كانت آخر جولة
    if (totalRounds && currentRound >= totalRounds) {
      console.log('✅ آخر جولة - عرض شاشة الإنهاء');
      setGameState('completed');
      return;
    }
    
    // الانتقال للجولة التالية
    const nextRound = currentRound + 1;
    console.log('➡️ الانتقال للجولة:', nextRound);
    
    navigate(`/student/competitions/${competitionId}/rounds/${nextRound}`, { replace: true });
    
    // إعادة تعيين الحالة
    setGameState('loading');
    setAnswer('');
    setResult(null);
    setCurrentNumberIndex(0);
    setSessionData(null);
    setRoundData(null);
    setCompetitionSessionId(null);
    hasStartedRef.current = false;
  };

  // الخروج والعودة
  const exitRound = () => {
    navigate(`/student/competitions/${competitionId}`);
  };

  // بدء الجولة عند تحميل المكون
  useEffect(() => {
    // جلب معلومات المسابقة أولاً
    fetchCompetitionInfo();
    
    if (competitionId && roundNumber && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startRound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId, roundNumber]);

  // شاشة التحميل
  if (gameState === 'loading') {
    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mb-4"></div>
          <p className="text-white text-xl">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // شاشة جاهز للبدء
  if (gameState === 'ready') {
    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
        <div className="text-center">
          <div className="mb-8">
            <TrophyIcon className="h-24 w-24 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-white mb-4">
              {t('competitions.roundNumber')} {roundNumber}
            </h1>
            <p className="text-xl text-gray-300">
              {t('flashAnzan.sessionReady')}
            </p>
          </div>
          
          <button
            onClick={startDisplaying}
            disabled={isLoading}
            className="px-12 py-4 bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl rounded-lg transition-colors disabled:opacity-50"
          >
            {t('flashAnzan.startNow')}
          </button>
        </div>
      </div>
    );
  }

  // شاشة عرض الأرقام
  if (gameState === 'playing') {
    const currentNumber = sessionData?.numbers?.[currentNumberIndex] || '';

    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-black px-3 py-2">
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-2">
          <div className="mx-auto flex min-h-0 w-full max-w-[min(100vw,56rem)] flex-1 flex-col items-center justify-center text-center">
            <FullscreenNumberDisplay value={currentNumber} tickKey={currentNumberIndex} />
            <div className="mt-4 shrink-0 text-base text-gray-400 sm:text-lg">
              {currentNumberIndex + 1} / {sessionData?.numbers?.length}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // شاشة إدخال الإجابة
  if (gameState === 'answer') {
    const numpadKeys = [
      ['7', '8', '9'],
      ['4', '5', '6'],
      ['1', '2', '3'],
      ['-', '0', 'backspace'],
    ];

    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto overscroll-contain bg-black px-4 py-3">
        <div className="w-full max-w-md px-4 text-center">
          <div className="mb-6">
            <CheckCircleIcon className="mx-auto mb-4 h-16 w-16 text-green-400" />
            <h2 className="mb-4 text-3xl font-bold text-white">{t('flashAnzan.enterAnswer')}</h2>
            <p className="mb-4 text-gray-300">{t('flashAnzan.calculateResult')}</p>
          </div>

          <div className="mb-4">
            <div className="flex min-h-[60px] w-full items-center justify-center rounded-lg border-2 border-gray-600 bg-gray-800 px-6 py-4 text-center text-3xl text-white">
              {answer || <span className="text-gray-500">{t('flashAnzan.yourAnswer')}</span>}
            </div>
          </div>

          <div className="mb-6 rounded-xl bg-gray-900 p-4">
            <div className="grid gap-2">
              {numpadKeys.map((row, rowIndex) => (
                <div key={rowIndex} className="flex justify-center gap-2">
                  {row.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleNumpadInput(key)}
                      className={`h-14 w-16 rounded-lg text-2xl font-bold transition-all ${
                        key === 'backspace'
                          ? 'bg-amber-600 text-white hover:bg-amber-500'
                          : key === '-'
                            ? 'bg-gray-600 text-white hover:bg-gray-500'
                            : 'bg-gray-700 text-white hover:bg-gray-600 active:scale-95'
                      }`}
                    >
                      {key === 'backspace' ? (
                        <svg className="mx-auto h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l2-2m0 0L7 8m-4 4l2-2m0 0l2-2"
                          />
                        </svg>
                      ) : (
                        key
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => handleNumpadInput('clear')}
              className="mt-2 w-full rounded-lg py-2 text-sm text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            >
              {t('flashAnzan.clear')}
            </button>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={exitRound}
              className="flex-1 rounded-lg bg-gray-700 px-6 py-3 font-semibold text-white transition-colors hover:bg-gray-600"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={submitAnswer}
              disabled={isLoading || !answer}
              className="flex-1 rounded-lg bg-green-500 px-6 py-3 font-bold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? t('flashAnzan.submitting') : t('flashAnzan.submitAnswer')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // شاشة إنهاء جميع الجولات
  if (gameState === 'completed') {
    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
        <div className="text-center max-w-2xl w-full px-6">
          <div className="mb-8">
            <TrophyIcon className="h-32 w-32 text-yellow-400 mx-auto mb-6 animate-bounce" />
            
            <h2 className="text-5xl font-bold text-white mb-4">
              {t('competitions.allRoundsCompleted')}
            </h2>
            <p className="text-2xl text-gray-300 mb-8">
              {t('competitions.congratulations')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-900 to-green-800 rounded-xl p-8 mb-8 border-2 border-green-600">
            <p className="text-xl text-white mb-2">
              {t('competitions.totalRounds')}
            </p>
            <p className="text-6xl font-bold text-yellow-400">
              {totalRounds}
            </p>
          </div>

          <button
            onClick={exitRound}
            className="w-full px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl rounded-lg transition-colors"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  // شاشة النتيجة
  if (gameState === 'result') {
    const isCorrect = result?.isCorrect;
    
    return (
      <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
        <div className="text-center max-w-2xl w-full px-6">
          <div className="mb-8">
            {isCorrect ? (
              <CheckCircleIcon className="h-24 w-24 text-green-400 mx-auto mb-6 animate-bounce" />
            ) : (
              <XCircleIcon className="h-24 w-24 text-red-400 mx-auto mb-6" />
            )}
            
            <h2 className={`text-5xl font-bold mb-6 ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
              {isCorrect ? t('flashAnzan.correct') : t('flashAnzan.incorrect')}
            </h2>
          </div>

          <div className="bg-gray-900 rounded-xl p-8 mb-8">
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-gray-400 text-sm mb-2">{t('flashAnzan.yourAnswer')}</p>
                <p className={`text-3xl font-bold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                  {result?.studentAnswer || answer}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-2">{t('flashAnzan.correctAnswer')}</p>
                <p className="text-3xl font-bold text-yellow-400">
                  {result?.correctAnswer}
                </p>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-800">
              <p className="text-gray-400 text-sm mb-2">{t('competitions.score')}</p>
              <p className="text-4xl font-bold text-yellow-400">
                {result?.score || '0'}
              </p>
            </div>
          </div>

          {/* أزرار التنقل */}
          <div className="flex gap-4">
            <button
              onClick={exitRound}
              className="flex-1 px-6 py-4 bg-gray-700 hover:bg-gray-600 text-white font-bold text-lg rounded-lg transition-colors"
            >
              {t('common.back')}
            </button>
            <button
              onClick={goToNextRound}
              className="flex-1 px-6 py-4 bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg rounded-lg transition-colors"
            >
              {totalRounds && parseInt(roundNumber) >= totalRounds 
                ? t('common.finish')
                : `${t('competitions.nextRound')} →`
              }
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CompetitionRound;
